import { useState } from 'react';

const usePinataUpload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Prefer environment variables. Do NOT hardcode secrets in source.
  const PINATA_JWT = process.env.REACT_APP_PINATA_JWT || '';
  const PINATA_API_KEY = process.env.REACT_APP_PINATA_API_KEY || '';
  const PINATA_SECRET_API_KEY = process.env.REACT_APP_PINATA_SECRET_API_KEY || '';
  const MAX_FILE_MB = 10;

  
  const testPinataConnection = async () => {
    try {
      const response = await fetch("https://api.pinata.cloud/data/testAuthentication", {
        method: 'GET',
        headers: PINATA_JWT ? {
          'Authorization': `Bearer ${PINATA_JWT}`
        } : {
          'pinata_api_key': PINATA_API_KEY,
          'pinata_secret_api_key': PINATA_SECRET_API_KEY
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Pinata connection test successful:', result);
        return true;
      } else {
        const body = await response.text().catch(() => '');
        console.error('Pinata connection test failed:', response.status, body);
        return false;
      }
    } catch (error) {
      console.error('Pinata connection test error:', error);
      return false;
    }
  };

  const uploadToPinata = async (file, institution, ocrData = null) => {
    setIsUploading(true);
    setUploadError(null);

    try {
      // Basic validations
      if (!file) throw new Error('No file provided');
      if (!institution) throw new Error('No institution provided');
      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB > MAX_FILE_MB) {
        throw new Error(`File too large: ${(sizeMB).toFixed(2)} MB (max ${MAX_FILE_MB} MB)`);
      }

      const formData = new FormData();
      // Explicitly set filename to help Pinata detect the file properly
      formData.append('file', file, file.name || 'certificate');

      // Create enhanced metadata with OCR data
      const sanitizedInstitution = institution.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
      
      const keyvalues = {
        institution: institution,
        folder: sanitizedInstitution,
        category: "academic_certificate",
        state: "Jharkhand",
        uploadDate: new Date().toISOString().split('T')[0]
      };

      // Add OCR data if available (staying within 10-field limit)
      if (ocrData) {
        if (ocrData.studentName && ocrData.studentName.trim()) {
          keyvalues.studentName = ocrData.studentName.trim().substring(0, 50); // Limit length
        }
        if (ocrData.rollNumber && ocrData.rollNumber.trim()) {
          keyvalues.rollNumber = ocrData.rollNumber.trim().substring(0, 20);
        }
        if (ocrData.certificateType && ocrData.certificateType.trim()) {
          keyvalues.certType = ocrData.certificateType.trim().substring(0, 30);
        }
        if (ocrData.marks && ocrData.marks.trim()) {
          keyvalues.marks = ocrData.marks.trim().substring(0, 15);
        }
        // Note: issueDate omitted to stay within 10-field limit
      }

      const pinataMetadata = JSON.stringify({
        name: `${sanitizedInstitution}/${file.name}`,
        keyvalues: keyvalues
      });
      formData.append('pinataMetadata', pinataMetadata);

      const pinataOptions = JSON.stringify({
        cidVersion: 0,
      });
      formData.append('pinataOptions', pinataOptions);

   
      const headers = PINATA_JWT ? {
        'Authorization': `Bearer ${PINATA_JWT}`
      } : {
        'pinata_api_key': PINATA_API_KEY,
        'pinata_secret_api_key': PINATA_SECRET_API_KEY
      };

      const response = await fetch("https://api.pinata.cloud/pinning/pinFileToIPFS", {
        method: 'POST',
        headers,
        body: formData
      });

      if (!response.ok) {
        let errorDetail = '';
        try {
          const asJson = await response.json();
          errorDetail = JSON.stringify(asJson);
        } catch {
          errorDetail = await response.text().catch(() => '');
        }
        console.error('Pinata API Error:', response.status, errorDetail);
        throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorDetail}`);
      }

      const result = await response.json();
      setIsUploading(false);
      return result;
    } catch (error) {
      console.error('Upload error:', error);
      setIsUploading(false);
      setUploadError(error.message);
      throw error;
    }
  };

  return { uploadToPinata, isUploading, uploadError, testPinataConnection };
};

export default usePinataUpload;
